import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  BookOpen,
  Award,
  Users,
  Cloud,
  Network,
  Shield,
  MapPin,
  Phone,
  Mail,
  FileText,
  Download,
  Layers,
  File,
  ImageIcon,
  GraduationCap,
  Users2,
  X,
  BriefcaseBusiness,
  BookOpenCheck,
  ChevronLeft,
  ChevronRight,
  BadgeCheck,
  Maximize2,
} from "lucide-react";

/* =========================================================
   ANIMATED DIVIDER
========================================================= */

const Divider = ({ delay = 0 }) => (
  <motion.div
    initial={{ scaleX: 0 }}
    whileInView={{ scaleX: 1 }}
    transition={{ duration: 0.8, delay }}
    viewport={{ once: true }}
    className="
      origin-left
      h-1
      bg-gradient-to-r
      from-blue-700
      to-blue-400
      rounded-full
      my-8
    "
  />
);

/* =========================================================
   STAFF DATA

   Only add confirmed academic information here.
========================================================= */

const staffList = [
  {
    id: 1,
    name: "Mr. Alao Kazeem Akinbola",
    role: "Head of Department (Chief Lecturer)",
    img: "/images/hodncc.jpg",

    specialization:
      "Computer Science, Networking & Cloud Computing",

    qualifications: [
      "Ph.D. Candidate",
      "M.Tech (FUTA)",
      "B.Sc. — University of Ilorin",
    ],

    researchInterests: [],

    professionalMemberships: [
      "CPN Certified Professional",
      "NCS Member & Committee Leader",
    ],

    achievements: [
      "33 Conference Papers",
      "26 Journal Publications",
      "Multiple TETFUND Grants",
      "15+ Years Teaching Experience",
      "AI Department NBTE Team Leader",
    ],

    email: "",

    academicProfile: `Mr. Alao Kazeem Akinbola is the pioneer Head of
    the Department of Networking and Cloud Computing. His academic
    activities cover teaching, research, academic leadership,
    innovation and professional development within the computing
    discipline.`,

    featured: true,
  },

  {
    id: 2,
    name: "Mr. Ibrahim Mohammed",
    role: "Lecturer (Networking)",
    img: "/images/staff1.jpg",

    specialization: "Computer Science",

    qualifications: [],
    researchInterests: [],
    professionalMemberships: [],
    achievements: [],
    email: "",

    academicProfile:
      "Additional academic profile information will be updated when provided by the department.",

    featured: false,
  },

  {
    id: 3,
    name: "Mr. Shamsudeen Iyanda",
    role: "Lecturer (Exam Officer)",
    img: "/images/staff2.jpg",

    specialization: "Computer Science",

    qualifications: [],
    researchInterests: [],
    professionalMemberships: [],
    achievements: [],
    email: "",

    academicProfile:
      "Additional academic profile information will be updated when provided by the department.",

    featured: false,
  },

  {
    id: 4,
    name: "Mr. Saba",
    role: "Lecturer (Networking)",
    img: "/images/staff3.jpg",

    specialization: "Computer Science",

    qualifications: [],
    researchInterests: [],
    professionalMemberships: [],
    achievements: [],
    email: "",

    academicProfile:
      "Additional academic profile information will be updated when provided by the department.",

    featured: false,
  },
];

/* =========================================================
   COURSE STRUCTURE
========================================================= */

const year1 = [
  "Mathematics for Computing",
  "Introduction to Networking",
  "Programming Fundamentals",
  "Digital Electronics",
  "Computer Systems",
  "Network Cabling & Infrastructure",
  "Professional Ethics",
];

const year2 = [
  "Advanced Routing & Switching",
  "Cloud Fundamentals (AWS/Azure)",
  "Network Security & Firewalls",
  "Virtualization Technologies",
  "Network Troubleshooting & Monitoring",
  "SIWES / Industrial Training",
  "Project Work",
];

/* =========================================================
   GALLERY DATA
========================================================= */

const galleryImages = [
  {
    img: "nccg1.jpg",
    title: "Department Gallery",
  },
  {
    img: "nccg2.jpg",
    title: "Department Gallery",
  },
  {
    img: "nccg3.jpg",
    title: "Department Gallery",
  },
  {
    img: "nccg4.jpg",
    title: "Department Gallery",
  },
  {
    img: "nccg5.jpg",
    title: "Department Gallery",
  },
  {
    img: "nccg6.jpg",
    title: "Department Gallery",
  },
];

/* =========================================================
   BROCHURE DATA
========================================================= */

const brochureSlides = [
  {
    img: "/images/brochure1.jpg",
    title: "Program Overview",
  },
  {
    img: "/images/brochure2.jpg",
    title: "Labs & Innovation Hub",
  },
  {
    img: "/images/brochure3.jpg",
    title: "Certifications Pathway",
  },
];

/* =========================================================
   STAFF ACADEMIC PROFILE MODAL
========================================================= */

const StaffAcademicProfileModal = ({ staff, onClose }) => {
  if (!staff) return null;

  const hasExtendedAcademicDetails =
    staff.qualifications?.length > 0 ||
    staff.researchInterests?.length > 0 ||
    staff.professionalMemberships?.length > 0 ||
    staff.achievements?.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="
        fixed
        inset-0
        z-[9999]
        flex
        items-center
        justify-center
        bg-slate-950/75
        backdrop-blur-sm
        p-3
        sm:p-5
        md:p-8
      "
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <motion.div
        initial={{
          opacity: 0,
          scale: 0.96,
          y: 30,
        }}
        animate={{
          opacity: 1,
          scale: 1,
          y: 0,
        }}
        exit={{
          opacity: 0,
          scale: 0.96,
          y: 20,
        }}
        transition={{
          duration: 0.28,
          ease: "easeOut",
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="staff-academic-profile-title"
        className="
          relative
          w-full
          max-w-6xl
          max-h-[92vh]
          overflow-y-auto
          rounded-2xl
          md:rounded-3xl
          bg-white
          shadow-2xl
        "
      >
        {/* CLOSE BUTTON */}

        <button
          type="button"
          onClick={onClose}
          aria-label="Close staff academic profile"
          className="
            absolute
            right-4
            top-4
            z-30
            flex
            h-11
            w-11
            items-center
            justify-center
            rounded-full
            border
            border-slate-200
            bg-white
            text-slate-700
            shadow-lg
            transition
            hover:bg-slate-100
            hover:text-blue-700
            focus:outline-none
            focus:ring-2
            focus:ring-blue-600
            focus:ring-offset-2
          "
        >
          <X size={21} />
        </button>

        <div className="grid md:grid-cols-[340px_minmax(0,1fr)]">
          {/* =================================================
              LEFT PROFILE PANEL
          ================================================= */}

          <aside
            className="
              relative
              overflow-hidden
              bg-gradient-to-b
              from-blue-950
              via-blue-900
              to-blue-800
              p-6
              pt-16
              text-white
              sm:p-8
              sm:pt-16
              md:p-8
            "
          >
            <div
              className="
                pointer-events-none
                absolute
                -left-20
                -top-20
                h-64
                w-64
                rounded-full
                bg-blue-400/10
                blur-3xl
              "
            />

            <div
              className="
                pointer-events-none
                absolute
                -bottom-16
                -right-16
                h-56
                w-56
                rounded-full
                bg-cyan-300/10
                blur-3xl
              "
            />

            <div className="relative z-10">
              {/* STAFF PHOTO */}

              <div
                className="
                  mx-auto
                  aspect-[4/5]
                  w-full
                  max-w-[280px]
                  overflow-hidden
                  rounded-2xl
                  border-4
                  border-white/20
                  bg-slate-200
                  shadow-2xl
                "
              >
                <img
                  src={staff.img}
                  alt={staff.name}
                  className="
                    h-full
                    w-full
                    object-cover
                    object-top
                  "
                />
              </div>

              {/* HOD BADGE */}

              {staff.featured && (
                <div className="mt-5 flex justify-center">
                  <span
                    className="
                      inline-flex
                      items-center
                      gap-2
                      rounded-full
                      border
                      border-blue-300/30
                      bg-white/10
                      px-4
                      py-2
                      text-xs
                      font-semibold
                      text-blue-50
                      backdrop-blur
                    "
                  >
                    <BadgeCheck size={16} />

                    Head of Department
                  </span>
                </div>
              )}

              {/* STAFF NAME */}

              <div className="mt-6 text-center md:text-left">
                <h2
                  className="
                    text-2xl
                    font-extrabold
                    leading-tight
                    text-white
                  "
                >
                  {staff.name}
                </h2>

                <p className="mt-2 text-sm font-medium text-blue-200">
                  {staff.role}
                </p>
              </div>

              {/* SPECIALIZATION */}

              {staff.specialization && (
                <div
                  className="
                    mt-6
                    flex
                    items-start
                    gap-3
                    rounded-xl
                    border
                    border-white/10
                    bg-white/5
                    p-4
                  "
                >
                  <BriefcaseBusiness
                    size={19}
                    className="mt-0.5 shrink-0 text-blue-300"
                  />

                  <div>
                    <p
                      className="
                        text-[11px]
                        font-semibold
                        uppercase
                        tracking-wider
                        text-blue-300
                      "
                    >
                      Area of Specialization
                    </p>

                    <p className="mt-1 text-sm leading-relaxed text-white">
                      {staff.specialization}
                    </p>
                  </div>
                </div>
              )}

              {/* EMAIL */}

              {staff.email && (
                <a
                  href={`mailto:${staff.email}`}
                  className="
                    mt-4
                    flex
                    items-center
                    gap-3
                    rounded-xl
                    border
                    border-white/10
                    bg-white/5
                    p-4
                    text-sm
                    text-blue-100
                    transition
                    hover:bg-white/10
                    hover:text-white
                  "
                >
                  <Mail
                    size={18}
                    className="shrink-0 text-blue-300"
                  />

                  <span className="break-all">
                    {staff.email}
                  </span>
                </a>
              )}
            </div>
          </aside>

          {/* =================================================
              RIGHT ACADEMIC PROFILE PANEL
          ================================================= */}

          <div className="min-w-0 p-6 sm:p-8 md:p-10 lg:p-12">
            {/* STAFF ACADEMIC PROFILE */}

            <section>
              <p
                className="
                  text-xs
                  font-bold
                  uppercase
                  tracking-[0.2em]
                  text-blue-700
                "
              >
                Department Staff
              </p>

              <h3
                id="staff-academic-profile-title"
                className="
                  mt-2
                  text-2xl
                  font-extrabold
                  text-slate-900
                  sm:text-3xl
                "
              >
                Staff Academic Profile
              </h3>

              <div
                className="
                  mt-4
                  h-1
                  w-16
                  rounded-full
                  bg-blue-700
                "
              />

              <p
                className="
                  mt-6
                  text-justify
                  leading-7
                  text-slate-600
                "
              >
                {staff.academicProfile}
              </p>
            </section>

            {/* =================================================
                ACADEMIC QUALIFICATIONS
            ================================================= */}

            {staff.qualifications?.length > 0 && (
              <section className="mt-9">
                <h4
                  className="
                    flex
                    items-center
                    gap-3
                    text-lg
                    font-bold
                    text-slate-900
                  "
                >
                  <span
                    className="
                      flex
                      h-9
                      w-9
                      items-center
                      justify-center
                      rounded-lg
                      bg-blue-50
                      text-blue-700
                    "
                  >
                    <GraduationCap size={20} />
                  </span>

                  Academic Qualifications
                </h4>

                <div className="mt-4 space-y-3">
                  {staff.qualifications.map(
                    (qualification, index) => (
                      <div
                        key={index}
                        className="
                          flex
                          items-start
                          gap-3
                          rounded-xl
                          border
                          border-slate-200
                          bg-slate-50
                          px-4
                          py-3.5
                        "
                      >
                        <span
                          className="
                            mt-2
                            h-2
                            w-2
                            shrink-0
                            rounded-full
                            bg-blue-600
                          "
                        />

                        <p
                          className="
                            text-sm
                            leading-6
                            text-slate-700
                          "
                        >
                          {qualification}
                        </p>
                      </div>
                    )
                  )}
                </div>
              </section>
            )}

            {/* =================================================
                RESEARCH INTERESTS
            ================================================= */}

            {staff.researchInterests?.length > 0 && (
              <section className="mt-9">
                <h4
                  className="
                    flex
                    items-center
                    gap-3
                    text-lg
                    font-bold
                    text-slate-900
                  "
                >
                  <span
                    className="
                      flex
                      h-9
                      w-9
                      items-center
                      justify-center
                      rounded-lg
                      bg-blue-50
                      text-blue-700
                    "
                  >
                    <BookOpenCheck size={19} />
                  </span>

                  Research Interests
                </h4>

                <div className="mt-4 flex flex-wrap gap-2">
                  {staff.researchInterests.map(
                    (interest, index) => (
                      <span
                        key={index}
                        className="
                          rounded-full
                          border
                          border-blue-100
                          bg-blue-50
                          px-4
                          py-2
                          text-sm
                          font-medium
                          text-blue-800
                        "
                      >
                        {interest}
                      </span>
                    )
                  )}
                </div>
              </section>
            )}

            {/* =================================================
                PROFESSIONAL MEMBERSHIPS
            ================================================= */}

            {staff.professionalMemberships?.length > 0 && (
              <section className="mt-9">
                <h4
                  className="
                    flex
                    items-center
                    gap-3
                    text-lg
                    font-bold
                    text-slate-900
                  "
                >
                  <span
                    className="
                      flex
                      h-9
                      w-9
                      items-center
                      justify-center
                      rounded-lg
                      bg-blue-50
                      text-blue-700
                    "
                  >
                    <Shield size={19} />
                  </span>

                  Professional Memberships
                </h4>

                <div className="mt-4 space-y-3">
                  {staff.professionalMemberships.map(
                    (membership, index) => (
                      <div
                        key={index}
                        className="
                          flex
                          items-start
                          gap-3
                          rounded-xl
                          border
                          border-slate-200
                          bg-white
                          px-4
                          py-3
                        "
                      >
                        <BadgeCheck
                          size={18}
                          className="
                            mt-0.5
                            shrink-0
                            text-blue-700
                          "
                        />

                        <span
                          className="
                            text-sm
                            leading-6
                            text-slate-700
                          "
                        >
                          {membership}
                        </span>
                      </div>
                    )
                  )}
                </div>
              </section>
            )}

            {/* =================================================
                ACHIEVEMENTS
            ================================================= */}

            {staff.achievements?.length > 0 && (
              <section className="mt-9">
                <h4
                  className="
                    flex
                    items-center
                    gap-3
                    text-lg
                    font-bold
                    text-slate-900
                  "
                >
                  <span
                    className="
                      flex
                      h-9
                      w-9
                      items-center
                      justify-center
                      rounded-lg
                      bg-amber-50
                      text-amber-600
                    "
                  >
                    <Award size={19} />
                  </span>

                  Academic & Professional Achievements
                </h4>

                <div
                  className="
                    mt-4
                    grid
                    gap-3
                    sm:grid-cols-2
                  "
                >
                  {staff.achievements.map(
                    (achievement, index) => (
                      <div
                        key={index}
                        className="
                          flex
                          items-start
                          gap-3
                          rounded-xl
                          border
                          border-amber-100
                          bg-amber-50/70
                          p-4
                        "
                      >
                        <Award
                          size={18}
                          className="
                            mt-0.5
                            shrink-0
                            text-amber-600
                          "
                        />

                        <span
                          className="
                            text-sm
                            font-medium
                            leading-6
                            text-slate-700
                          "
                        >
                          {achievement}
                        </span>
                      </div>
                    )
                  )}
                </div>
              </section>
            )}

            {/* =================================================
                PROFILE PENDING NOTICE
            ================================================= */}

            {!hasExtendedAcademicDetails && (
              <div
                className="
                  mt-9
                  rounded-2xl
                  border
                  border-blue-100
                  bg-blue-50
                  p-5
                "
              >
                <div className="flex items-start gap-3">
                  <Users
                    size={20}
                    className="
                      mt-0.5
                      shrink-0
                      text-blue-700
                    "
                  />

                  <div>
                    <h4 className="font-semibold text-blue-950">
                      Additional Academic Information
                    </h4>

                    <p
                      className="
                        mt-1
                        text-sm
                        leading-6
                        text-blue-800
                      "
                    >
                      Academic qualifications, research interests,
                      professional memberships and achievements
                      will appear here when additional information
                      is supplied by the department.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* =========================================================
   GALLERY LIGHTBOX
========================================================= */

const GalleryLightbox = ({
  image,
  images,
  onClose,
  onChange,
}) => {
  if (!image) return null;

  const currentIndex = image.index;

  const showPrevious = () => {
    const previousIndex =
      currentIndex === 0
        ? images.length - 1
        : currentIndex - 1;

    onChange({
      ...images[previousIndex],
      index: previousIndex,
    });
  };

  const showNext = () => {
    const nextIndex =
      currentIndex === images.length - 1
        ? 0
        : currentIndex + 1;

    onChange({
      ...images[nextIndex],
      index: nextIndex,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="
        fixed
        inset-0
        z-[10000]
        flex
        items-center
        justify-center
        bg-slate-950/95
        p-3
        backdrop-blur-md
        sm:p-5
        md:p-8
      "
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <motion.div
        initial={{
          opacity: 0,
          scale: 0.96,
          y: 20,
        }}
        animate={{
          opacity: 1,
          scale: 1,
          y: 0,
        }}
        exit={{
          opacity: 0,
          scale: 0.96,
        }}
        transition={{
          duration: 0.25,
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Department gallery image viewer"
        className="
          relative
          w-full
          max-w-6xl
        "
      >
        {/* CLOSE */}

        <button
          type="button"
          onClick={onClose}
          aria-label="Close gallery"
          className="
            absolute
            right-3
            top-3
            z-30
            flex
            h-11
            w-11
            items-center
            justify-center
            rounded-full
            bg-white
            text-slate-800
            shadow-xl
            transition
            hover:bg-blue-50
            hover:text-blue-700
            focus:outline-none
            focus:ring-2
            focus:ring-blue-500
          "
        >
          <X size={22} />
        </button>

        {/* PREVIOUS */}

        {images.length > 1 && (
          <button
            type="button"
            onClick={showPrevious}
            aria-label="Previous gallery image"
            className="
              absolute
              left-3
              top-1/2
              z-20
              flex
              h-12
              w-12
              -translate-y-1/2
              items-center
              justify-center
              rounded-full
              bg-white/95
              text-slate-800
              shadow-xl
              transition
              hover:scale-105
              hover:bg-white
              hover:text-blue-700
              focus:outline-none
              focus:ring-2
              focus:ring-blue-500
              md:left-5
            "
          >
            <ChevronLeft size={26} />
          </button>
        )}

        {/* NEXT */}

        {images.length > 1 && (
          <button
            type="button"
            onClick={showNext}
            aria-label="Next gallery image"
            className="
              absolute
              right-3
              top-1/2
              z-20
              flex
              h-12
              w-12
              -translate-y-1/2
              items-center
              justify-center
              rounded-full
              bg-white/95
              text-slate-800
              shadow-xl
              transition
              hover:scale-105
              hover:bg-white
              hover:text-blue-700
              focus:outline-none
              focus:ring-2
              focus:ring-blue-500
              md:right-5
            "
          >
            <ChevronRight size={26} />
          </button>
        )}

        {/* IMAGE CONTAINER */}

        <div
          className="
            overflow-hidden
            rounded-2xl
            bg-black
            shadow-2xl
          "
        >
          <div
            className="
              flex
              min-h-[260px]
              items-center
              justify-center
              bg-black
            "
          >
            <motion.img
              key={image.img}
              initial={{
                opacity: 0,
                scale: 0.98,
              }}
              animate={{
                opacity: 1,
                scale: 1,
              }}
              transition={{
                duration: 0.25,
              }}
              src={`/images/${image.img}`}
              alt={`${image.title} ${currentIndex + 1}`}
              className="
                max-h-[78vh]
                w-full
                object-contain
              "
            />
          </div>

          {/* LIGHTBOX FOOTER */}

          <div
            className="
              flex
              flex-col
              gap-3
              bg-slate-900
              px-5
              py-4
              text-white
              sm:flex-row
              sm:items-center
              sm:justify-between
            "
          >
            <div>
              <h3 className="font-semibold">
                {image.title}
              </h3>

              <p className="mt-1 text-xs text-slate-400">
                Image {currentIndex + 1} of {images.length}
              </p>
            </div>

            <p
              className="
                text-xs
                text-slate-400
                sm:text-right
              "
            >
              Department of Networking & Cloud Computing
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

const NetworkingCloud = () => {
  const [selectedStaff, setSelectedStaff] = useState(null);

  const [
    selectedGalleryImage,
    setSelectedGalleryImage,
  ] = useState(null);

  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 30,
    },

    visible: {
      opacity: 1,
      y: 0,
    },
  };

  /* =======================================================
     MODAL / LIGHTBOX KEYBOARD CONTROL
  ======================================================= */

  useEffect(() => {
    const modalOpen =
      selectedStaff || selectedGalleryImage;

    if (!modalOpen) {
      return undefined;
    }

    const originalOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      /* ESCAPE CLOSE */

      if (event.key === "Escape") {
        setSelectedStaff(null);
        setSelectedGalleryImage(null);

        return;
      }

      /* GALLERY KEYBOARD NAVIGATION */

      if (selectedGalleryImage) {
        if (event.key === "ArrowLeft") {
          const currentIndex =
            selectedGalleryImage.index;

          const previousIndex =
            currentIndex === 0
              ? galleryImages.length - 1
              : currentIndex - 1;

          setSelectedGalleryImage({
            ...galleryImages[previousIndex],
            index: previousIndex,
          });
        }

        if (event.key === "ArrowRight") {
          const currentIndex =
            selectedGalleryImage.index;

          const nextIndex =
            currentIndex === galleryImages.length - 1
              ? 0
              : currentIndex + 1;

          setSelectedGalleryImage({
            ...galleryImages[nextIndex],
            index: nextIndex,
          });
        }
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      document.body.style.overflow =
        originalOverflow;

      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [selectedStaff, selectedGalleryImage]);

  return (
    <div className="min-h-screen bg-gray-50 text-slate-900">
      {/* =================================================
          DEPARTMENT NAVIGATION
      ================================================= */}

      <nav
        className="
          sticky
          top-0
          z-50
          flex
          items-center
          gap-6
          overflow-x-auto
          whitespace-nowrap
          bg-white/90
          px-4
          py-3
          shadow-md
          backdrop-blur-md
          md:px-20
        "
      >
        <div className="flex-1 font-semibold text-blue-700">
          Networking & Cloud — Federal Polytechnic Bida
        </div>

        <div className="flex gap-4">
          <a
            href="#brief"
            className="hover:text-blue-500"
          >
            Overview
          </a>

          <a
            href="#why"
            className="hover:text-blue-500"
          >
            Why Study
          </a>

          <a
            href="#admission"
            className="hover:text-blue-500"
          >
            Admission
          </a>

          <a
            href="#prospects"
            className="hover:text-blue-500"
          >
            Careers
          </a>

          <a
            href="#certifications"
            className="hover:text-blue-500"
          >
            Certifications
          </a>

          <a
            href="#staff"
            className="hover:text-blue-500"
          >
            Meet Staff
          </a>

          <a
            href="#courses"
            className="hover:text-blue-500"
          >
            Course Structure
          </a>

          <a
            href="#brochure"
            className="hover:text-blue-500"
          >
            Brochure
          </a>

          <a
            href="#gallery"
            className="hover:text-blue-500"
          >
            Gallery
          </a>

          <a
            href="#contact"
            className="hover:text-blue-500"
          >
            Contact
          </a>
        </div>
      </nav>

      {/* =================================================
          HERO
      ================================================= */}

      <header
        className="
          relative
          flex
          h-[340px]
          items-center
          justify-center
          overflow-hidden
          md:h-[420px]
        "
      >
        <img
          src="/images/nccb.jpg"
          alt="Networking and Cloud Computing Department"
          className="
            absolute
            inset-0
            h-full
            w-full
            scale-105
            object-cover
          "
        />

        <div
          className="
            absolute
            inset-0
            bg-gradient-to-b
            from-black/70
            to-black/30
          "
        />

        <div
          className="
            relative
            z-10
            px-6
            text-center
            text-white
          "
        >
          <motion.h1
            initial={{
              opacity: 0,
              y: -20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.8,
            }}
            className="
              text-3xl
              font-extrabold
              md:text-5xl
            "
          >
            Department of Networking & Cloud Computing
          </motion.h1>

          <motion.p
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            transition={{
              delay: 0.2,
            }}
            className="
              mx-auto
              mt-3
              max-w-2xl
            "
          >
            Fostering innovation, practical skills and
            professional excellence in Networking, Cloud and
            Telecommunication.
          </motion.p>

          <div
            className="
              mt-6
              flex
              flex-wrap
              justify-center
              gap-4
            "
          >
            <a
              href="admissions"
              className="
                inline-flex
                items-center
                gap-2
                rounded-lg
                bg-blue-700
                px-5
                py-3
                text-white
                shadow
                transition
                hover:bg-blue-600
              "
            >
              Apply Now
            </a>

            <a
              href="/brochure.pdf"
              download
              className="
                inline-flex
                items-center
                gap-2
                rounded-lg
                bg-white
                px-5
                py-3
                text-blue-700
                shadow
              "
            >
              <Download size={19} />

              Brochure (PDF)
            </a>
          </div>
        </div>
      </header>

      {/* =================================================
          MAIN
      ================================================= */}

      <main
        className="
          space-y-12
          px-6
          py-12
          md:px-20
        "
      >
        {/* =================================================
            DEPARTMENT BRIEF
        ================================================= */}

        <motion.section
          id="brief"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
          }}
          className="
            rounded-2xl
            bg-white
            p-8
            shadow-lg
          "
        >
          <h2
            className="
              flex
              items-center
              gap-3
              text-2xl
              font-bold
            "
          >
            <BookOpen className="text-blue-700" />

            The Department in Brief
          </h2>

          <Divider />

          <p
            className="
              text-justify
              leading-relaxed
              text-gray-700
            "
          >
            Sequel to the unbundled Higher National Diploma
            (HND) in Computer Science by the National Board for
            Technical Education (NBTE), the Department of
            Networking and Cloud Computing came to being in the
            2023/2024 Academic Session. The pioneer Head of
            Department in person of Mr. Alao Kazeem Akinbola
            was appointed by the management of the institution
            on the 8th of November, 2024 to oversee the smooth
            running of the academic and administrative
            activities of the department. The department kicked
            start with 7 academic staff, of which one is a Ph.D
            holder in Computer Science, with one other currently
            on Ph.D programme in Computer Science, and three are
            currently on Master programme in Computer Science.
            The department graduated the first set of 90
            students in October 2025 at the end of 2024/2025
            academic session.
          </p>
        </motion.section>

        {/* =================================================
            HOD SPEECH
        ================================================= */}

        <motion.section
          id="hodspeech"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
          }}
          className="
            relative
            flex
            flex-col
            items-start
            gap-10
            overflow-hidden
            rounded-2xl
            bg-white
            p-8
            shadow-lg
            md:flex-row
          "
        >
          <div
            className="
              pointer-events-none
              absolute
              inset-0
              bg-gradient-to-b
              from-white
              via-blue-50/40
              to-white
            "
          />

          <div
            className="
              pointer-events-none
              absolute
              inset-0
              bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.08),transparent_70%)]
            "
          />

          <motion.div
            initial={{
              opacity: 0,
              y: 20,
            }}
            animate={{
              opacity: 0.6,
              y: 0,
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            className="
              absolute
              right-10
              top-10
              h-10
              w-10
              rounded-full
              bg-blue-400/40
              blur-xl
            "
          />

          <motion.div
            initial={{
              opacity: 0,
              y: -20,
            }}
            animate={{
              opacity: 0.5,
              y: 0,
            }}
            transition={{
              duration: 2,
              delay: 1,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            className="
              absolute
              bottom-10
              left-10
              h-16
              w-16
              rounded-full
              bg-blue-300/40
              blur-xl
            "
          />

          <div
            className="
              absolute
              left-0
              top-0
              h-64
              w-64
              rounded-full
              bg-gradient-to-b
              from-blue-200/60
              to-blue-400/40
              blur-3xl
            "
          />

          <div
            className="
              relative
              z-10
              flex
              w-full
              justify-center
              md:w-1/3
            "
          >
            <motion.img
              src="/images/hodncc.jpg"
              alt="Head of Department"
              className="
                h-52
                w-52
                rounded-full
                border-4
                border-white
                object-cover
                shadow-xl
              "
              whileHover={{
                rotate: 3,
                scale: 1.05,
              }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 10,
              }}
            />
          </div>

          <div className="relative z-10 flex-1">
            <h2
              className="
                flex
                items-center
                gap-3
                text-2xl
                font-bold
              "
            >
              <Users className="text-blue-700" />

              HOD Speech
            </h2>

            <Divider />

            <div
              className="
                mb-6
                flex
                flex-wrap
                gap-3
              "
            >
              <div className="rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800 shadow">
                🏅 33 Conference Papers
              </div>

              <div className="rounded-full bg-yellow-100 px-3 py-1 text-sm font-medium text-yellow-800 shadow">
                📚 26 Journal Publications
              </div>

              <div className="rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800 shadow">
                🎓 Multiple TETFUND Grants
              </div>

              <div className="rounded-full bg-purple-100 px-3 py-1 text-sm font-medium text-purple-800 shadow">
                ⭐ 15+ Years Teaching Experience
              </div>

              <div className="rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-800 shadow">
                🏆 CPN Certified Professional
              </div>

              <div className="rounded-full bg-indigo-100 px-3 py-1 text-sm font-medium text-indigo-800 shadow">
                🛡️ NCS Member & Committee Leader
              </div>

              <div className="rounded-full bg-teal-100 px-3 py-1 text-sm font-medium text-teal-800 shadow">
                🎖️ AI Department NBTE Team Leader
              </div>
            </div>

            <div
              className="
                mb-6
                rounded-md
                border-l-4
                border-blue-600
                bg-blue-50
                p-4
                shadow-sm
              "
            >
              <p className="font-medium italic text-blue-900">
                “Our commitment is to inspire innovation, build
                competence, and shape ICT professionals who can
                transform the world.”
              </p>
            </div>

            <p className="text-justify leading-relaxed text-gray-700">
              Dear students, faculty members, and esteemed
              visitors, I warmly welcome you to the Department
              of Networking and Cloud Computing. This department
              was founded on the principles of excellence,
              innovation, and practical skill development, and
              we remain committed to producing graduates who are
              globally competitive and technically exceptional.
            </p>

            <p className="mt-4 text-justify leading-relaxed text-gray-700">
              In today’s digital world, Networking and Cloud
              Computing form the backbone of virtually every
              modern organization. Our mission is to ensure that
              every student acquires not only theoretical
              knowledge but also hands-on experience through our
              Innovation Hub, industry certifications,
              research-driven seminars, and collaborations with
              top ICT institutions. We strive to build
              confident, ethical, and solution-driven
              professionals who can excel anywhere in the world.
            </p>

            <p className="mt-4 text-justify leading-relaxed text-gray-700">
              I encourage you to explore, inquire, innovate, and
              take full advantage of the opportunities this
              department provides. Together, we will continue
              shaping the leaders and trailblazers of tomorrow’s
              technology-driven society.
            </p>

            <p className="mt-6 font-semibold text-blue-700">
              — Mr. Alao Kazeem Akinbola
            </p>

            <p className="text-gray-600">
              Head of Department
            </p>
          </div>
        </motion.section>

        {/* =================================================
            WHY STUDY
        ================================================= */}

        <motion.section
          id="why"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
          }}
          className="
            rounded-2xl
            bg-blue-900
            p-8
            text-white
            shadow-lg
          "
        >
          <h2
            className="
              flex
              items-center
              gap-3
              text-2xl
              font-bold
            "
          >
            <Award className="text-yellow-300" />

            Why Study Here
          </h2>

          <Divider />

          <p className="text-justify leading-relaxed text-gray-100">
            As a Higher National Diploma (HND) based department,
            it is worthy of note that the department is poised
            towards a purposeful, impactful and resourceful
            knowledge acquisition for the students of the
            department, through the execution of educational and
            innovative programmes aimed at producing employable
            and self-reliant graduates, with expertise and
            practical skills in the fields of Networking, Cloud
            Computing, and Telecommunication.
          </p>

          <ul
            className="
              mt-6
              grid
              gap-3
              text-gray-100
              md:grid-cols-2
            "
          >
            <li className="flex items-start gap-3">
              <ImageIcon className="mt-1" />
              Departmental seminars & industry research papers
              for capacity building.
            </li>

            <li className="flex items-start gap-3">
              <FileText className="mt-1" />
              Enrollment for professional certifications
              (Cisco, CompTIA).
            </li>

            <li className="flex items-start gap-3">
              <Layers className="mt-1" />
              Innovation Hub with hands-on networking & cloud
              toolkits.
            </li>

            <li className="flex items-start gap-3">
              <Users2 className="mt-1" />
              Intra & inter-class debates, quizzes and
              competitions.
            </li>

            <li className="flex items-start gap-3">
              <MapPin className="mt-1" />
              Excursions to related industries for on-site
              learning.
            </li>

            <li className="flex items-start gap-3">
              <Award className="mt-1" />
              Awards, scholarships and sponsorships for
              students.
            </li>
          </ul>
        </motion.section>

        {/* =================================================
            ADMISSION
        ================================================= */}

        <motion.section
          id="admission"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
          }}
          className="
            rounded-2xl
            bg-white
            p-8
            shadow-lg
          "
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <Users className="text-blue-700" />

            Admission Requirements
          </h2>

          <Divider />

          <p className="text-justify leading-relaxed text-gray-700">
            Candidates applying for admission to study
            Networking and Cloud Computing are required to have
            a minimum of five credits in O’Level at not more
            than two sittings with Mathematics, English Language
            and Physics as compulsory subjects. In addition
            applicants must have any other two subjects from
            Chemistry, Biology/Agricultural Science,
            Geography/Economics/Commerce.
          </p>
        </motion.section>

        <Divider delay={0.1} />

        {/* =================================================
            JOB PROSPECTS
        ================================================= */}

        <motion.section
          id="prospects"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
          }}
          className="
            rounded-2xl
            bg-white
            p-8
            shadow-lg
          "
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <Network className="text-blue-700" />

            Job Prospects
          </h2>

          <Divider />

          <p className="text-justify leading-relaxed text-gray-700">
            Networking and Cloud Computing are two
            interconnected fields that form the backbone of
            modern computing and communication systems.
            Graduates can find roles across many industries
            including Telecommunication, Banking & Finance, Oil
            & Gas, Education, Aviation, Military, Broadcasting,
            Manufacturing, and Healthcare.
          </p>

          <div className="mt-6 grid gap-8 md:grid-cols-2">
            <div>
              <h3 className="mb-2 text-lg font-semibold text-blue-700">
                Job Descriptions (Networking)
              </h3>

              <ul className="list-inside list-disc space-y-2 text-gray-700">
                <li>Network Architect</li>
                <li>Network Engineer</li>
                <li>Network Administrator</li>
                <li>Cybersecurity Specialist</li>
                <li>Network Security Engineer</li>
                <li>Wireless Network Engineer</li>
                <li>Network Analyst</li>
              </ul>
            </div>

            <div>
              <h3 className="mb-2 text-lg font-semibold text-blue-700">
                Job Descriptions (Cloud)
              </h3>

              <ul className="list-inside list-disc space-y-2 text-gray-700">
                <li>Cloud Architect</li>
                <li>Cloud Engineer</li>
                <li>Cloud Security Engineer</li>
                <li>Cloud Migration Specialist</li>
                <li>Cloud Operations Engineer</li>
                <li>DevOps Engineer</li>
                <li>Cloud Data Engineer</li>
              </ul>
            </div>
          </div>
        </motion.section>

        {/* =================================================
            CERTIFICATIONS
        ================================================= */}

        <motion.section
          id="certifications"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
          }}
          className="
            rounded-2xl
            bg-blue-50
            p-8
            shadow-lg
          "
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <Shield className="text-blue-700" />

            Certification / Professional Courses
          </h2>

          <Divider />

          <p className="text-justify leading-relaxed text-gray-800">
            Certification and professional courses in
            Networking and Cloud Computing offer immense
            benefits including improved job prospects,
            increased earning potential and enhanced skills. We
            emphasize certifications from recognized bodies
            such as Cisco, CompTIA, AWS, Google Cloud and
            Microsoft Azure.
          </p>

          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <div>
              <h4 className="mb-2 font-semibold text-blue-700">
                Cisco
              </h4>

              <ul className="list-inside list-disc text-gray-700">
                <li>CCNA — Entry-level networking</li>
                <li>CCNP — Professional networking</li>
                <li>CCIE — Expert-level internetworking</li>
                <li>CCDE — Network design</li>
              </ul>
            </div>

            <div>
              <h4 className="mb-2 font-semibold text-blue-700">
                CompTIA & Cloud
              </h4>

              <ul className="list-inside list-disc text-gray-700">
                <li>CompTIA A+, Network+, Security+</li>
                <li>AWS / Azure Fundamentals</li>
                <li>Google Cloud Associate</li>
                <li>
                  Vendor-specific specialty certifications
                </li>
              </ul>
            </div>
          </div>
        </motion.section>

        {/* =================================================
            MEET OUR STAFF
        ================================================= */}

        <motion.section
          id="staff"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
          }}
          className="
            rounded-2xl
            bg-white
            p-6
            shadow-lg
            md:p-8
          "
        >
          <div>
            <h2
              className="
                flex
                items-center
                gap-3
                text-2xl
                font-bold
                text-slate-900
              "
            >
              <Users2 className="text-blue-700" />

              Meet Our Staff
            </h2>

            <p
              className="
                mt-2
                max-w-3xl
                text-sm
                leading-6
                text-gray-600
                sm:text-base
              "
            >
              Meet the academic professionals supporting
              teaching, research, innovation and student
              development in the Department of Networking and
              Cloud Computing.
            </p>
          </div>

          <Divider />

          <div
            className="
              grid
              grid-cols-1
              gap-6
              sm:grid-cols-2
              lg:grid-cols-4
            "
          >
            {staffList.map((staff) => (
              <motion.button
                type="button"
                key={staff.id}
                onClick={() =>
                  setSelectedStaff(staff)
                }
                whileHover={{
                  y: -7,
                }}
                whileTap={{
                  scale: 0.98,
                }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 20,
                }}
                aria-label={`View academic profile of ${staff.name}`}
                className="
                  group
                  relative
                  overflow-hidden
                  rounded-2xl
                  border
                  border-slate-200
                  bg-white
                  text-left
                  shadow-sm
                  transition-all
                  duration-300
                  hover:border-blue-200
                  hover:shadow-xl
                  focus:outline-none
                  focus:ring-2
                  focus:ring-blue-600
                  focus:ring-offset-2
                "
              >
                {/* STAFF IMAGE */}

                <div
                  className="
                    relative
                    h-[310px]
                    overflow-hidden
                    bg-slate-100
                  "
                >
                  <img
                    src={staff.img}
                    alt={staff.name}
                    loading="lazy"
                    className="
                      h-full
                      w-full
                      object-cover
                      object-top
                      transition-transform
                      duration-500
                      group-hover:scale-105
                    "
                  />

                  <div
                    className="
                      absolute
                      inset-x-0
                      bottom-0
                      h-44
                      bg-gradient-to-t
                      from-slate-950
                      via-slate-950/65
                      to-transparent
                    "
                  />

                  {staff.featured && (
                    <span
                      className="
                        absolute
                        left-4
                        top-4
                        inline-flex
                        items-center
                        gap-1.5
                        rounded-full
                        bg-blue-700
                        px-3
                        py-1.5
                        text-[11px]
                        font-bold
                        uppercase
                        tracking-wide
                        text-white
                        shadow-lg
                      "
                    >
                      <BadgeCheck size={14} />

                      HOD
                    </span>
                  )}

                  <div
                    className="
                      absolute
                      bottom-0
                      left-0
                      right-0
                      p-5
                      text-white
                    "
                  >
                    <h3 className="text-lg font-bold leading-tight">
                      {staff.name}
                    </h3>

                    <p className="mt-1.5 text-sm font-medium text-blue-200">
                      {staff.role}
                    </p>
                  </div>
                </div>

                {/* CARD LOWER SECTION */}

                <div className="p-5">
                  {staff.specialization && (
                    <div
                      className="
                        flex
                        min-h-[48px]
                        items-start
                        gap-2.5
                        text-sm
                        text-slate-600
                      "
                    >
                      <BriefcaseBusiness
                        size={17}
                        className="
                          mt-0.5
                          shrink-0
                          text-blue-700
                        "
                      />

                      <span className="leading-5">
                        {staff.specialization}
                      </span>
                    </div>
                  )}

                  <div
                    className="
                      mt-5
                      flex
                      items-center
                      justify-between
                      border-t
                      border-slate-100
                      pt-4
                      text-sm
                      font-bold
                      text-blue-700
                    "
                  >
                    <span>
                      View Academic Profile
                    </span>

                    <ChevronRight
                      size={19}
                      className="
                        transition-transform
                        duration-300
                        group-hover:translate-x-1
                      "
                    />
                  </div>
                </div>
              </motion.button>
            ))}
          </div>

          <p className="mt-6 text-center text-xs text-slate-500">
            Click or tap any staff card to view the staff
            academic profile.
          </p>
        </motion.section>

        {/* =================================================
            COURSE STRUCTURE
        ================================================= */}

        <motion.section
          id="courses"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
          }}
          className="
            rounded-2xl
            bg-white
            p-8
            shadow-lg
          "
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <GraduationCap className="text-blue-700" />

            Course Structure (HND)
          </h2>

          <Divider />

          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-lg bg-gray-50 p-4">
              <h3 className="mb-3 text-lg font-semibold">
                Year 1 (Core Modules)
              </h3>

              <ol className="list-inside list-decimal space-y-1 text-gray-700">
                {year1.map((module, index) => (
                  <li key={index}>
                    {module}
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg bg-gray-50 p-4">
              <h3 className="mb-3 text-lg font-semibold">
                Year 2 (Core Modules)
              </h3>

              <ol className="list-inside list-decimal space-y-1 text-gray-700">
                {year2.map((module, index) => (
                  <li key={index}>
                    {module}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </motion.section>

        {/* =================================================
            BROCHURE
        ================================================= */}

        <motion.section
          id="brochure"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
          }}
          className="
            rounded-2xl
            bg-blue-900
            p-6
            text-white
            shadow-lg
          "
        >
          <div
            className="
              flex
              flex-col
              gap-4
              sm:flex-row
              sm:items-center
              sm:justify-between
            "
          >
            <h2 className="flex items-center gap-3 text-2xl font-bold">
              <File className="text-white" />

              Admission Brochure
            </h2>

            <a
              href="/brochure.pdf"
              download
              className="
                inline-flex
                items-center
                justify-center
                gap-2
                rounded-md
                bg-white
                px-4
                py-2
                text-blue-900
              "
            >
              <Download size={18} />

              Download PDF
            </a>
          </div>

          <Divider />

          <div className="no-scrollbar mt-4 overflow-x-auto py-4">
            <div className="flex w-max gap-4">
              {brochureSlides.map(
                (slide, index) => (
                  <motion.div
                    key={index}
                    whileHover={{
                      scale: 1.02,
                    }}
                    className="
                      min-w-[280px]
                      rounded-lg
                      bg-white/10
                      p-4
                      md:min-w-[360px]
                    "
                  >
                    <img
                      src={slide.img}
                      alt={slide.title}
                      className="
                        h-40
                        w-full
                        rounded-md
                        object-cover
                        shadow-sm
                      "
                    />

                    <h4 className="mt-3 font-semibold">
                      {slide.title}
                    </h4>

                    <p className="mt-1 text-sm text-gray-100">
                      Short highlight about this brochure page
                      to entice prospective students.
                    </p>
                  </motion.div>
                )
              )}
            </div>
          </div>
        </motion.section>

        {/* =================================================
            DEPARTMENT GALLERY - CLICK TO VIEW
        ================================================= */}

        <motion.section
          id="gallery"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
          }}
          className="
            rounded-2xl
            bg-white
            p-6
            shadow-lg
            md:p-8
          "
        >
          <div>
            <h2 className="flex items-center gap-3 text-2xl font-bold">
              <Cloud className="text-blue-700" />

              Department Gallery
            </h2>

            <p
              className="
                mt-2
                max-w-3xl
                text-sm
                leading-6
                text-slate-600
                sm:text-base
              "
            >
              Explore photographs from departmental activities,
              practical sessions, events and facilities.
            </p>
          </div>

          <Divider />

          <div
            className="
              grid
              grid-cols-1
              gap-4
              sm:grid-cols-2
              lg:grid-cols-4
            "
          >
            {galleryImages.map(
              (image, index) => (
                <motion.button
                  type="button"
                  key={image.img}
                  onClick={() =>
                    setSelectedGalleryImage({
                      ...image,
                      index,
                    })
                  }
                  whileHover={{
                    y: -5,
                  }}
                  whileTap={{
                    scale: 0.98,
                  }}
                  aria-label={`View gallery image ${
                    index + 1
                  }`}
                  className="
                    group
                    relative
                    overflow-hidden
                    rounded-xl
                    border
                    border-slate-200
                    bg-slate-100
                    text-left
                    shadow-sm
                    transition
                    hover:border-blue-200
                    hover:shadow-lg
                    focus:outline-none
                    focus:ring-2
                    focus:ring-blue-600
                    focus:ring-offset-2
                  "
                >
                  <div
                    className="
                      relative
                      h-52
                      overflow-hidden
                    "
                  >
                    <img
                      src={`/images/${image.img}`}
                      alt={`Department gallery image ${
                        index + 1
                      }`}
                      loading="lazy"
                      className="
                        h-full
                        w-full
                        object-cover
                        transition-transform
                        duration-500
                        group-hover:scale-110
                      "
                    />

                    {/* IMAGE OVERLAY */}

                    <div
                      className="
                        absolute
                        inset-0
                        bg-gradient-to-t
                        from-slate-950/80
                        via-slate-950/5
                        to-transparent
                      "
                    />

                    {/* VIEW ICON */}

                    <div
                      className="
                        absolute
                        right-3
                        top-3
                        flex
                        h-10
                        w-10
                        items-center
                        justify-center
                        rounded-full
                        bg-white/95
                        text-blue-700
                        shadow-lg
                        transition
                        group-hover:scale-110
                      "
                    >
                      <Maximize2 size={17} />
                    </div>

                    {/* CLICK TO VIEW */}

                    <div
                      className="
                        absolute
                        inset-0
                        flex
                        items-center
                        justify-center
                      "
                    >
                      <span
                        className="
                          rounded-full
                          bg-white/95
                          px-4
                          py-2
                          text-sm
                          font-semibold
                          text-blue-800
                          opacity-0
                          shadow-lg
                          transition-all
                          duration-300
                          group-hover:opacity-100
                        "
                      >
                        Click to View
                      </span>
                    </div>

                    {/* IMAGE NUMBER */}

                    <div
                      className="
                        absolute
                        bottom-0
                        left-0
                        right-0
                        p-4
                        text-white
                      "
                    >
                      <p
                        className="
                          text-xs
                          font-medium
                          text-slate-200
                        "
                      >
                        Gallery Photo {index + 1}
                      </p>
                    </div>
                  </div>
                </motion.button>
              )
            )}
          </div>

          <p className="mt-5 text-center text-xs text-slate-500">
            Click or tap any photograph to view the full image.
          </p>
        </motion.section>

        {/* =================================================
            CONTACT
        ================================================= */}

        <motion.section
          id="contact"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
          }}
          className="
            rounded-2xl
            bg-white
            p-8
            shadow-lg
          "
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <MapPin className="text-blue-700" />

            Contact the Department
          </h2>

          <Divider />

          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <p className="text-gray-700">
                <strong>Address:</strong>{" "}
                Federal Polytechnic, Bida, Niger State, Nigeria
              </p>

              <p className="mt-2 flex items-center gap-2 text-gray-700">
                <Mail className="text-blue-700" />

                networkingcloud@fedpolybida.edu.ng
              </p>

              <p className="mt-2 flex items-center gap-2 text-gray-700">
                <Phone className="text-blue-700" />

                +234 803 631 8009
              </p>
            </div>

            <div>
              <iframe
                title="Federal Polytechnic Bida location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3931.203503579147!2d6.0079231!3d9.0391016!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104966b7f2e9b195%3A0x18c8eec7a2fae37c!2sFederal+Polytechnic+Bida%2C+Doko+Road%2C+Bida%2C+Niger+State!5e0!3m2!1sen!2sus!4v1694784000000!5m2!1sen!2sus"
                width="100%"
                height="220"
                loading="lazy"
                className="
                  rounded-md
                  border-0
                  shadow-sm
                "
              />
            </div>
          </div>
        </motion.section>
      </main>

      {/* =================================================
          STAFF ACADEMIC PROFILE
      ================================================= */}

      <AnimatePresence>
        {selectedStaff && (
          <StaffAcademicProfileModal
            key={`staff-${selectedStaff.id}`}
            staff={selectedStaff}
            onClose={() =>
              setSelectedStaff(null)
            }
          />
        )}
      </AnimatePresence>

      {/* =================================================
          GALLERY LIGHTBOX
      ================================================= */}

      <AnimatePresence mode="wait">
        {selectedGalleryImage && (
          <GalleryLightbox
            key="department-gallery-lightbox"
            image={selectedGalleryImage}
            images={galleryImages}
            onClose={() =>
              setSelectedGalleryImage(null)
            }
            onChange={
              setSelectedGalleryImage
            }
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default NetworkingCloud;
